/**
 * CONTRIBUTE BACK (EXPERT-ROOMS.md §16) — on an Expert Network installed from the registry: offer what THIS company
 * added on top of the base (its own procedures and rules) back to the registry's curators. Off until a workspace owner
 * turns it on. Every item is shown exactly as it would leave — identifiers replaced with placeholders; anything holding
 * a secret can't be sent. Nothing else ever leaves: no trained answers, facts, people or files; the contributor is
 * anonymous. Shown only when there is an upstream (``available``).
 */
import React, { useEffect, useState } from 'react';
import { getExpertContributions, postExpertContribution } from '@stepflow/ui-shared/services/networkRegistryApi';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const STATUS = { pending: 'Waiting for the curators', accepted: 'Accepted', declined: 'Declined' };

export default function ExpertContribSection({ network }) {
  const [v, setV] = useState(null);
  const [pick, setPick] = useState({});
  const [note, setNote] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const eid = network?.networkId;

  useEffect(() => {
    if (!network?.expertNetwork || !eid) return undefined;
    let alive = true;
    getExpertContributions(eid).then((d) => { if (alive) setV(d); }).catch(() => {});
    return () => { alive = false; };
  }, [eid, network?.expertNetwork]);

  if (!v || !v.available) return null;
  const chosen = Object.keys(pick).filter((k) => pick[k]);

  const act = async (body) => {
    setBusy(true); setErr('');
    try {
      const d = await postExpertContribution(eid, body);
      setV(d);
      if (body.action === 'send') { setPick({}); setNote(''); setConsent(false); }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-3" data-testid="expert-contrib">
      <h3 className="text-sm font-semibold text-gray-800">Contribute back</h3>
      <p className="text-xs text-gray-500">
        Offer what your company added to this expert (its own procedures and rules) to the registry it came from, so
        everyone using it benefits. Only what you pick leaves, exactly as shown — your account ids, emails and other
        identifiers are replaced, secrets can never be sent, and the curators don&apos;t see who you are.
      </p>
      <div className="flex items-center gap-2 text-xs">
        <span className={`rounded px-1.5 py-px font-medium ${v.optedIn ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}
          data-testid="expert-contrib-state">{v.optedIn ? 'On' : 'Off'}</span>
        {v.canOptIn ? (
          <button type="button" disabled={busy} className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
            data-testid="expert-contrib-toggle" onClick={() => act({ action: 'optIn', on: !v.optedIn })}>
            {v.optedIn ? 'Turn off' : 'Turn on'}
          </button>
        ) : !v.optedIn ? <span className="text-gray-400">A workspace owner turns it on.</span> : null}
      </div>
      {v.optedIn ? (
        (v.candidates || []).length ? (
          <ul className="space-y-1.5" data-testid="expert-contrib-candidates">
            {v.candidates.map((c) => (
              <li key={c.id} className={`rounded-md border p-2 text-xs ${c.sendable ? 'border-gray-200' : 'border-rose-100 bg-rose-50/40'}`}>
                <label className="flex items-start gap-2">
                  <input type="checkbox" disabled={!c.sendable || !v.canSend || busy} checked={!!pick[c.id]}
                    data-testid={`expert-contrib-pick-${c.id}`} onChange={(e) => setPick({ ...pick, [c.id]: e.target.checked })} />
                  <span className="min-w-0">
                    <span className="font-medium text-gray-800">{c.kind === 'procedure' ? 'Procedure' : 'Rule'} — {c.title}</span>
                    <span className="ml-1 text-gray-400">({c.change}{c.team ? ` · ${c.team}` : ''})</span>
                    <pre className="mt-1 whitespace-pre-wrap font-sans text-[11px] text-gray-700">{c.text}</pre>
                    {c.replaced?.length ? <span className="text-[10px] text-gray-500">Replaced: {c.replaced.join(', ')}</span> : null}
                    {!c.sendable ? <span className="block text-[10px] text-rose-700">Holds {c.secrets?.join(', ') || 'a secret'} — can't be sent.</span> : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        ) : <p className="text-xs text-gray-400">Nothing of your own on top of the base yet.</p>
      ) : null}
      {v.optedIn && v.canSend && (v.candidates || []).length ? (
        <div className="space-y-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="A note for the curators (optional)"
            className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs" data-testid="expert-contrib-note" />
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="expert-contrib-consent" />
            I&apos;m sending exactly the items ticked above, as shown.
          </label>
          <button type="button" disabled={busy || !chosen.length || !consent} data-testid="expert-contrib-send"
            className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}
            onClick={() => act({ action: 'send', items: chosen, note, consent: true })}>
            Send {chosen.length || ''} to the registry
          </button>
        </div>
      ) : null}
      {(v.sent || []).length ? (
        <ul className="divide-y divide-gray-100 text-xs" data-testid="expert-contrib-sent">
          {v.sent.map((s) => (
            <li key={s.contributionId} className="py-1 text-gray-600">
              {s.items} item{s.items === 1 ? '' : 's'} · {STATUS[s.status] || s.status}
              {s.decisionNote ? ` — “${s.decisionNote}”` : ''}
            </li>
          ))}
        </ul>
      ) : null}
      {err ? <p className="text-xs text-rose-600">{err}</p> : null}
    </section>
  );
}
